<template>
  <b-form novalidate @submit.prevent="onOk">
    <b-modal
      v-model="open"
      :title="title"
      :size="isClosingCheckout ? 'lg' : 'md'"
      :ok-title="isClosingCheckout ? 'Fechar caixa' : 'Abrir caixa'"
      :ok-variant="isClosingCheckout ? 'primary' : 'success'"
      cancel-title="Cancelar"
      centered
      scrollable
      body-class="checkout-modal-body"
      footer-class="checkout-modal-footer"
      :ok-disabled="
        openingContextLoading ||
        closingBalanceLoading ||
        openingBlocked ||
        checkoutStoreLoading ||
        !storeConfig ||
        Boolean(checkoutStoreError)
      "
      @ok.prevent="onOk"
      @cancel="onCancel"
      @close="onCancel"
    >
      <b-alert v-if="checkoutStoreError" show variant="danger">
        {{ checkoutStoreError }}
      </b-alert>
      <div v-else-if="checkoutStoreLoading" class="text-center py-3">
        <b-spinner small class="mr-2" />
        Carregando configurações da loja...
      </div>
      <CheckoutForm
        v-else-if="storeConfig"
        ref="form"
        v-model="form"
        :operation="operation"
        :store-config="storeConfig"
        :opening-context="openingContext"
        :is-admin="isAdmin"
        :has-active-products="hasActiveProducts"
        @submit="onSubmit"
      />
    </b-modal>
  </b-form>
</template>

<script>
import { mapState } from 'vuex'
import services from '@/services'
import { CHECKOUT_OPERATIONS } from '@/constants'
import { formatCurrencyInput } from '@/helpers/numbers'
import CheckoutForm from './CheckoutForm'

export default {
  name: 'CheckoutModal',
  components: {
    CheckoutForm
  },
  props: {
    items: {
      type: Array,
      default: () => []
    },
    value: {
      type: Boolean
    },
    title: {
      type: String,
      default: ''
    },
    operation: {
      type: String,
      required: true
    },
    checkout: {
      type: Object,
      default: () => ({})
    },
    hasActiveProducts: {
      type: Boolean,
      default: true
    },
    storeId: {
      type: [Number, String],
      default: null
    },
    isAdmin: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      checkoutStore: null,
      checkoutStoreLoading: false,
      checkoutStoreError: null,
      checkoutStoreRequestId: 0,
      openingContext: null,
      openingContextLoading: false,
      closingBalanceLoading: false,
      form: this.createForm()
    }
  },
  computed: {
    ...mapState('store', ['currentStore']),
    open: {
      set(value) {
        this.$emit('input', value)
      },
      get() {
        return this.value
      }
    },
    isClosingCheckout() {
      return this.operation === CHECKOUT_OPERATIONS.CLOSE
    },
    isOpeningCheckout() {
      return this.operation === CHECKOUT_OPERATIONS.OPEN
    },
    openingBlocked() {
      return this.isOpeningCheckout && this.openingContext?.canOpen === false
    },
    operationStoreId() {
      return Number(
        this.isClosingCheckout
          ? this.checkout?.storeId
          : this.storeId || this.currentStore?.id
      )
    },
    storeConfig() {
      if (Number(this.checkout?.store?.id) === this.operationStoreId) {
        return this.checkout.store
      }
      if (Number(this.checkoutStore?.id) === this.operationStoreId) {
        return this.checkoutStore
      }
      if (
        this.currentStore &&
        Number(this.currentStore.id) === this.operationStoreId
      ) {
        return this.currentStore
      }

      return null
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(value) {
        if (value && this.isOpeningCheckout) this.loadOpeningContext()
        if (value && this.isClosingCheckout) this.loadClosingBalance()
      }
    },
    checkout: {
      immediate: true,
      handler(value) {
        this.form = this.createForm(value)
      }
    },
    operationStoreId: {
      immediate: true,
      handler(value, previousValue) {
        this.loadCheckoutStore()
        if (
          previousValue !== undefined &&
          this.value &&
          this.isOpeningCheckout
        ) {
          this.loadOpeningContext()
        }
      }
    }
  },
  methods: {
    createForm(checkout = {}) {
      const isClosing =
        this.operation === CHECKOUT_OPERATIONS.CLOSE && Boolean(checkout?.id)

      return {
        ...checkout,
        openingValue: null,
        openingOverrideEnabled: false,
        openingOverrideReason: null,
        openingProductCount: null,
        closingValue: isClosing
          ? formatCurrencyInput(checkout.total || 0)
          : null,
        closingExpectedValue: isClosing ? Number(checkout.total || 0) : null,
        closingDifferenceReason: null,
        closingProductCount: null,
        closingNotebookPhotos: [],
        closingNotebookDescriptions: [],
        machineReceiptPhotos: [],
        machineReceiptDescriptions: []
      }
    },
    async loadOpeningContext() {
      const storeId = this.operationStoreId
      if (!storeId) {
        this.openingContext = {
          canOpen: false,
          reason: 'Nao foi possivel identificar a loja para abrir o caixa.'
        }
        return
      }

      this.openingContextLoading = true
      try {
        const context = await services.checkouts.fetchOpeningContext({
          storeId
        })
        if (storeId !== this.operationStoreId) return
        this.openingContext = context
        this.form.openingValue =
          context.expectedOpeningValue === null
            ? null
            : formatCurrencyInput(context.expectedOpeningValue)
        this.form.openingOverrideEnabled = false
        this.form.openingOverrideReason = null
      } catch (error) {
        if (storeId !== this.operationStoreId) return
        this.openingContext = {
          canOpen: false,
          reason:
            error?.response?.data?.message ||
            'Nao foi possivel carregar o valor da abertura.'
        }
      } finally {
        if (storeId === this.operationStoreId) {
          this.openingContextLoading = false
        }
      }
    },
    async loadClosingBalance() {
      if (!this.checkout?.id) return

      this.closingBalanceLoading = true
      try {
        const checkout = await services.checkouts.fetchCheckoutById({
          id: this.checkout.id
        })
        const expected = Number(checkout.total || 0)
        this.form.closingExpectedValue = expected
        this.form.closingValue = formatCurrencyInput(expected)
        this.form.closingDifferenceReason = null
      } catch (error) {
        this.$root.$toast.error(
          error?.response?.data?.message ||
            'Nao foi possivel atualizar o saldo esperado do caixa.'
        )
      } finally {
        this.closingBalanceLoading = false
      }
    },
    async loadCheckoutStore() {
      const requestId = ++this.checkoutStoreRequestId
      const storeId = this.operationStoreId
      this.checkoutStore = null
      this.checkoutStoreLoading = false
      this.checkoutStoreError = null
      if (!storeId) {
        this.checkoutStoreError =
          'Não foi possível identificar a loja do caixa.'
        return
      }
      if (this.storeConfig) return

      this.checkoutStoreLoading = true
      try {
        const store = await services.stores.fetchStoreById(storeId)
        if (requestId !== this.checkoutStoreRequestId) return
        this.checkoutStore = store
      } catch (error) {
        if (requestId !== this.checkoutStoreRequestId) return
        this.checkoutStoreError =
          error?.response?.data?.message ||
          'Não foi possível carregar as configurações da loja. Reabra a tela para tentar novamente.'
      } finally {
        if (requestId === this.checkoutStoreRequestId) {
          this.checkoutStoreLoading = false
        }
      }
    },
    onOk() {
      if (
        this.openingContextLoading ||
        this.closingBalanceLoading ||
        this.checkoutStoreLoading ||
        this.checkoutStoreError ||
        !this.storeConfig
      ) {
        return
      }
      if (this.checkout == null) {
        if (this.items.some(({ closedAt }) => !closedAt)) {
          this.$root.$toast.error('Feche o caixa atual antes de abrir outro.')
          return
        }
      }
      if (this.openingBlocked) {
        this.$root.$toast.error(this.openingContext.reason)
        return
      }
      this.$refs.form.handleSubmit()
    },
    onCancel() {
      this.$emit('cancel')
    },
    onSubmit() {
      this.$emit('ok', this.form)
    }
  }
}
</script>

<style>
.checkout-modal-body {
  background: #f8f9fa;
  padding: 1.25rem;
}

.checkout-modal-footer {
  background: #fff;
  border-top: 1px solid #e9ecef;
  padding: 0.875rem 1.25rem;
}

.checkout-modal-footer .btn {
  min-width: 7.5rem;
}

@media (max-width: 575.98px) {
  .checkout-modal-body {
    padding: 0.875rem;
  }

  .checkout-modal-footer {
    display: grid;
    gap: 0.5rem;
    grid-template-columns: 1fr 1fr;
    padding: 0.75rem;
  }

  .checkout-modal-footer .btn {
    margin: 0;
    min-width: 0;
    width: 100%;
  }
}
</style>
